import { useMemo } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { ConciergeProduct } from './conciergeProduct.types';
import type { ConciergeServiceCategory } from './conciergeConfig';
import { useInvitationConciergeProductsData, useSaveTheDateConciergeProductsData } from './conciergeProductsData';
import { seededShuffle } from '@apps/card/utils/seededRandom';

/**
 * Each category's concierge products in a seed-determined order: the same seed always gives the
 * same order, so a server render and its hydration agree (PRNT-5494) and every caller passing that
 * seed sees one order. For finding a product by name or id, use `useConciergeProductLookup`.
 */
export const useConciergeProducts = (seed: string): { conciergeProducts: Record<ConciergeServiceCategory, ReadonlyArray<ConciergeProduct>> } => {
  const invitationProducts = useInvitationConciergeProductsData();
  const saveTheDateProducts = useSaveTheDateConciergeProductsData();

  const conciergeProducts = useMemo(
    () => ({
      [StationeryTemplateCategoryEnum.invitation]: seededShuffle(Object.values(invitationProducts) as ConciergeProduct[], seed),
      [StationeryTemplateCategoryEnum.saveTheDate]: seededShuffle(Object.values(saveTheDateProducts) as ConciergeProduct[], seed),
    }),
    [invitationProducts, saveTheDateProducts, seed],
  );

  return { conciergeProducts };
};
